<template>
  <div>
    <!-- Table Container Card -->
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Table Top -->
        <b-row class="mb-2">
          <!-- Search -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-start">
            <b-form-input
              v-model="searchQuery"
              placeholder="Search customers..."
              style="max-width: 300px"
            />
          </b-col>

          <!-- Actions -->
          <b-col cols="12" md="6" class="d-flex align-items-center justify-content-md-end justify-content-start flex-wrap" style="gap: 10px;">
            <!-- Watch Now Button -->
            <b-button variant="outline-success" @click="openWatchVideo">
              <feather-icon icon="PlayIcon" size="16" class="mr-50" />
              Watch Now
            </b-button>

            <!-- Export Button -->
            <b-button
              variant="outline-secondary"
              @click="exportCustomers"
              :disabled="isExporting"
            >
              <b-spinner v-if="isExporting" small class="mr-50" />
              <feather-icon v-else icon="DownloadIcon" size="16" class="mr-50" />
              {{ isExporting ? 'Exporting...' : 'Export' }}
            </b-button>

            <!-- Bulk Upload Button -->
            <b-button variant="outline-primary" @click="onClickImport">
              <feather-icon icon="UploadIcon" size="16" class="mr-50" />
              Import
            </b-button>

            <b-button
              variant="danger"
              @click="onClickBulkDelete"
              v-if="selectedRows && selectedRows.length > 0"
              :disabled="bulkDeleteLoading"
            >
              <b-spinner v-if="bulkDeleteLoading" small class="mr-50" />
              <feather-icon v-else icon="TrashIcon" size="16" class="mr-50" />
              Delete All
            </b-button>

            <!-- Add Customer Button -->
            <b-button variant="primary" @click="$router.push('/apps/fbr-invoice/customer/add')">
              <feather-icon icon="PlusIcon" size="16" class="mr-50" />
              Add Customer
            </b-button>
          </b-col>
        </b-row>
      </div>

      <!-- Select All Summary -->
      <b-alert v-if="selectAll && selectedRows.length > 0" variant="primary" show class="mx-2 mb-2">
        <div class="alert-body font-weight-bold d-flex align-items-center justify-content-between">
          <span>
            <feather-icon icon="InfoIcon" class="mr-50" />
            You have selected all {{ selectedRows.length }} matching customers.
          </span>
          <b-button size="sm" variant="outline-primary" @click="selectAll = false; toggleSelectAll()">
            Clear Selection
          </b-button>
        </div>
      </b-alert>

      <b-table
        ref="refCustomerListTable"
        class="position-relative"
        :items="fetchCustomers"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="No matching records found"
        :busy="isTableLoading"
        sticky-header="true"
        :selectable="true"
        @row-selected="onRowSelected"
        select-mode="multi"
      >
        <!-- Loading Skeleton -->
        <template #table-busy>
          <table-skeleton :columns="tableColumns" :rows="5" />
        </template>

        <!-- Custom Header Checkbox for select all -->
        <template #head(select)>
          <b-form-checkbox
            v-model="selectAll"
            class="custom-control-primary"
            @change="toggleSelectAll"
          />
        </template>

        <!-- Column: Select -->
        <template #cell(select)="data">
          <b-form-checkbox
            :checked="data.rowSelected"
            class="custom-control-primary"
            @change="data.toggleDetails(); $refs.refCustomerListTable.selectRow(data.index)"
            @input="(val) => { if(val) $refs.refCustomerListTable.selectRow(data.index); else $refs.refCustomerListTable.unselectRow(data.index); }"
          />
        </template>

        <!-- Column: Code -->
        <template #cell(code)="data">
          <span
            class="font-weight-bold text-primary cursor-pointer hover-effect"
            @click="$router.push(`/apps/fbr-invoice/customer/view/${data.item.id}`)"
            title="Click to view Customer details"
          >
            {{ data.value }}
          </span>
        </template>

        <!-- Column: Name -->
        <template #cell(name)="data">
          <div class="d-flex">
            <h6 class="mb-0">{{ data.value }}</h6>
          </div>
        </template>

        <!-- Column: Short Name -->
        <template #cell(shortName)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Cell # -->
        <template #cell(cellNumber)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Category -->
        <template #cell(category)="data">
          <b-badge :variant="getCategoryVariant(data.value)" pill class="category-badge">
            {{ data.value || 'Unregistered' }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(isActive)="data">
          <b-badge :variant="getStatusVariant(data.value)" pill class="status-badge">
            {{ data.value ? 'Active' : 'Inactive' }}
          </b-badge>
        </template>

        <!-- Column: Email -->
        <template #cell(email)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: NTN # -->
        <template #cell(ntnNumber)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: STRN # -->
        <template #cell(gstNumber)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Address -->
        <template #cell(address)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Province -->
        <template #cell(province)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: CNIC Number -->
        <template #cell(cnicNumber)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Contact Person -->
        <template #cell(contactPerson)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Whatsapp # -->
        <template #cell(whatsappOrUAN)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Website -->
        <template #cell(website)="data">
          <span class="text-muted">{{ data.value || '-' }}</span>
        </template>

        <!-- Column: Commission % -->
        <template #cell(commissionPercent)="data">
          <span class="font-weight-bold">{{ data.value ? `${data.value}%` : '-' }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <div class="d-flex align-items-center justify-content-center">
            <!-- View Button -->
            <b-button
              variant="flat-primary"
              class="btn-icon rounded-circle"
              title="View Customer"
              @click="$router.push(`/apps/fbr-invoice/customer/view/${data.item.id}`)"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>

            <!-- Edit Button -->
            <b-button
              variant="flat-info"
              class="btn-icon rounded-circle"
              title="Edit Customer"
              @click="$router.push(`/apps/fbr-invoice/customer/edit/${data.item.id}`)"
            >
              <feather-icon icon="EditIcon" size="16" />
            </b-button>

            <!-- Delete Button -->
            <b-button
              variant="flat-danger"
              class="btn-icon rounded-circle"
              title="Delete Customer"
              @click="onDeleteCustomer(data.item.id)"
            >
              <feather-icon icon="TrashIcon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>

      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <!-- Per Page -->
            <label class="mb-0 mr-1">Entries</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              style="width: 80px"
            />
            <span class="text-muted"
              >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalCustomers"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>

    <!-- Import Modal -->
    <customer-list-import-modal
      :show.sync="isImportModalVisible"
      :loading="importLoading"
      @bulkImport="bulkImport"
    />
  </div>
</template>

<script>
import store from '@/store';
import { onUnmounted, ref } from '@vue/composition-api';
import {
  BAvatar,
  BBadge,
  BButton,
  BCard,
  BCol,
  BDropdown,
  BDropdownItem,
  BFormInput,
  BLink,
  BMedia,
  BPagination,
  BRow,
  BTable,
  BModal,
  BAlert,
  BProgress,
  BSpinner,
  BFormCheckbox,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import customerStoreModule from './CustomerStoreModule';
import useCustomerList from './useCustomerList';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import TableSkeleton from '@/components/common/skeletons/TableSkeleton.vue';
import { useToast } from 'vue-toastification/composition';
import CustomerListImportModal from './CustomerListImportModal.vue';
import ResourceListHeader from '@/components/common/list/ResourceListHeader.vue';

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BModal,
    BAlert,
    BProgress,
    BSpinner,
    BFormCheckbox,
    CustomerListImportModal,
    vSelect,
    ToastificationContent,
    TableSkeleton,
    ResourceListHeader,
  },
  setup(props, { root }) {
    const CUSTOMER_APP_STORE_MODULE_NAME = 'fbr-invoice-customer';

    // Register module
    if (!store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME))
      store.registerModule(CUSTOMER_APP_STORE_MODULE_NAME, customerStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CUSTOMER_APP_STORE_MODULE_NAME))
        store.unregisterModule(CUSTOMER_APP_STORE_MODULE_NAME);
    });

    const isExporting = ref(false);

    // Bulk Upload State

    const isImportModalVisible = ref(false);
    const importLoading = ref(false);

    // Multi-select and bulk delete references
    const bulkDeleteLoading = ref(false);

    const onClickBulkDelete = async () => {
      if (!selectedRows.value || selectedRows.value.length === 0) return;

      const confirmed = await root.$bvModal.msgBoxConfirm(`Are you sure you want to delete ${selectedRows.value.length} selected customers?`, {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      });

      if (!confirmed) return;

      bulkDeleteLoading.value = true;
      try {
        const ids = selectedRows.value.map(row => row.id).join(',');
        await store.dispatch('fbr-invoice-customer/deleteCustomer', { id: ids });
        
        root.$bvToast.toast('Customers deleted successfully', { title: 'Success', variant: 'success', solid: true });
        refetchData();
      } catch (error) {
        root.$bvToast.toast('Error deleting customers', { title: 'Error', variant: 'danger', solid: true });
      } finally {
        bulkDeleteLoading.value = false;
        selectAll.value = false;
      }
    };

    const onDeleteCustomer = async (id) => {
      try {
        // Show confirmation dialog using root.$bvModal
        const confirmed = await new Promise((resolve) => {
          root.$bvModal
            .msgBoxConfirm('Are you sure you want to delete this Customer?', {
              title: 'Please Confirm',
              size: 'sm',
              buttonSize: 'sm',
              okVariant: 'danger',
              okTitle: 'YES',
              cancelTitle: 'NO',
              footerClass: 'p-2',
              hideHeaderClose: false,
              centered: true,
            })
            .then((value) => resolve(value));
        });

        if (!confirmed) return;

        // Call delete action
        await store.dispatch('fbr-invoice-customer/deleteCustomer', { id });

        // Show success toast
        root.$bvToast.toast('Customer deleted successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
        });

        // Refetch data to update the table
        refetchData();
      } catch (error) {
        console.error('Error deleting customer:', error);

        // Show user-friendly error message
        let errorMessage = 'Error deleting Customer. Please try again.';

        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }

        // Show error toast
        root.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        });
      }
    };

    const {
      isTableLoading,
      fetchCustomers,
      tableColumns,
      perPage,
      currentPage,
      totalCustomers,
      dataMeta,
      perPageOptions,
      searchQuery,
      refCustomerListTable,
      selectedRows,
      selectAll,
      toggleSelectAll,
      refetchData,
    } = useCustomerList();

    const exportCustomers = async () => {
      isExporting.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-customer/exportCustomers', {
          searchTerm: searchQuery.value || '',
        });

        const filePath = response.data && response.data.filePath;
        if (!filePath) {
          throw new Error('Invalid response format from server');
        }

        const baseURL = process.env.VUE_APP_BASE_URL;
        const downloadURL = `${baseURL}${filePath}`;

        const link = document.createElement('a');
        link.href = downloadURL;
        link.download = filePath.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Export successful!',
            text: response.message || 'Customers exported successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        });
      } catch (error) {
        console.error('Export error:', error);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Export failed',
            text:
              (error.response && error.response.data && error.response.data.message)
              || error.message
              || 'Failed to export customers',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        isExporting.value = false;
      }
    };

    // Bulk Upload Functions

    const onRowSelected = (rows) => {
      // If selectAll is active, we don't want local page navigation to override our global selection
      if (selectAll.value) return;

      selectedRows.value = rows;
    };

    const toast = useToast();

    // Helper functions for status and category variants
    const getStatusVariant = (isActive) => {
      return isActive ? 'light-success' : 'light-danger';
    };

    const getCategoryVariant = (category) => {
      const variants = {
        Registered: 'light-primary',
        Unregistered: 'light-secondary',
        Individual: 'light-info',
        Company: 'light-warning',
        Government: 'light-success',
      };
      return variants[category] || 'light-secondary';
    };

    const onClickImport = () => {
      console.log('=== onClickImport');

      isImportModalVisible.value = true;
    };

    const bulkImport = async (data, onComplete) => {
      importLoading.value = true;

      try {
        const response = await store.dispatch('fbr-invoice-customer/bulkUploadCustomer', data);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Successfully imported ${data.length} invoices`,
          },
        });

        // Refresh the data
        refetchData();

        // Call the completion callback with success
        if (onComplete) {
          onComplete(true, null);
        }
      } catch (error) {
        console.error('Bulk import error:', error);

        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertCircleIcon',
            variant: 'danger',
            text: error.message || 'Failed to import invoices',
          },
        });

        // Call the completion callback with error
        if (onComplete) {
          onComplete(false, error);
        }
      } finally {
        importLoading.value = false;
      }
    };

    // Define customerVideoLink
    const customerVideoLink = 'https://cdn.cloudious.net/file-1756979138300-134087374.mp4';

    // Watch video function
    const openWatchVideo = () => {
      window.open(customerVideoLink, '_blank');
    };

    return {
      isTableLoading,
      tableColumns,
      perPage,
      currentPage,
      totalCustomers,
      dataMeta,
      perPageOptions,
      searchQuery,
      refCustomerListTable,

      fetchCustomers,
      refetchData,
      onDeleteCustomer,

      // Bulk Upload
      isImportModalVisible,
      importLoading,
      onClickImport,
      bulkImport,

      // Export
      isExporting,
      exportCustomers,

      // Helper functions
      getStatusVariant,
      getCategoryVariant,

      // Resource List Header
      customerVideoLink,
      openWatchVideo,

      // Multi-select & Bulk Delete
      selectedRows,
      selectAll,
      bulkDeleteLoading,
      onRowSelected,
      toggleSelectAll,
      onClickBulkDelete,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.upload-area {
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #7367f0 !important;
    background-color: #f8f9fa;
  }

  &.border-primary {
    border-color: #7367f0 !important;
    background-color: #f8f9fa;
  }
}

// Skeleton loader styles
.skeleton-row {
  .skeleton-cell {
    padding: 0.75rem;

    .skeleton-item {
      height: 20px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: loading 1.5s infinite;
      border-radius: 4px;
    }
  }
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

// Enhanced badge styling for status and category
.status-badge,
.category-badge {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.375rem 0.75rem;
  transition: all 0.2s ease-in-out;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

// Table sticky header styling
.b-table-sticky-header {
  max-height: 80vh !important;
}

// Clickable code styling
.cursor-pointer {
  cursor: pointer;
}

.hover-effect {
  transition: all 0.2s ease-in-out;

  &:hover {
    color: #0056b3 !important;
    text-decoration: underline;
    transform: translateY(-1px);
  }
}

// Light variant badge styles
.badge.badge-pill {
  &.light-success {
    background-color: #d1f2eb;
    color: #0f5132;
    border: 1px solid #badbcc;
  }

  &.light-danger {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
  }

  &.light-primary {
    background-color: #e3f2fd;
    color: #0d47a1;
    border: 1px solid #bbdefb;
  }

  &.light-secondary {
    background-color: #f5f5f5;
    color: #424242;
    border: 1px solid #e0e0e0;
  }

  &.light-info {
    background-color: #e1f5fe;
    color: #01579b;
    border: 1px solid #b3e5fc;
  }

  &.light-warning {
    background-color: #fff3e0;
    color: #e65100;
    border: 1px solid #ffcc02;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
